/* The desk's alerts: a sound, a desktop notice and the tab title when work
 * arrives, and the bell — what the platform has told this agency.
 *
 * WHY. A family's booking goes to independent workers and agencies in the same
 * round, and the first to accept wins (owner ruling 2026-10-22). An agency that
 * learns of a request by happening to look at the Requests page loses it. The
 * server already records OFFER_RECEIVED, QUOTE_WON, ITEM_CANCELLED,
 * ASSIGNMENT_DECLINED, JOIN_REQUESTED, STAFF_OFFER_EXPIRED, AGENCY_UNSTAFFED
 * and the approval decisions for the agency's people (internal/notify, app
 * AGENCY_DESKTOP); nothing on the desk showed them.
 *
 * WHAT RINGS. The rail's own counts (/agency/profile, re-read by App.tsx), and
 * only when one GOES UP, never because work is still waiting — a tone every
 * fifteen seconds is a tone the room mutes:
 *   - `open_offers`, `join_requests` or `overdue_to_staff` up: the full chime
 *     and a desktop notice. Someone must act — an offer the first agency wins,
 *     a worker waiting to be confirmed, a won job past its staffing deadline
 *     with nobody on it (000079, the family is about to be let down).
 *   - `checkout_overdue` up (000097): the same chime and a desktop notice — a
 *     nurse is still checked in after her shift; the agency checks her out.
 *   - only `to_staff` up: a SOFT single note, no desktop notice. A win raises
 *     it and the dispatcher who accepted already knows, but a colleague at
 *     another desk of the same agency does not, and the job needs somebody.
 * The same rule as the console's SOS alert (apps/admin/src/shell/SosAlert.tsx),
 * including that browsers refuse sound until the first click or key press.
 * The rules are written down in docs/agency-app-contract.md (Alerts).
 *
 * THE BELL is GET /me/notifications for this app, read when it opens: a record
 * of what was said, never the state (notify N6). Each row opens the page where
 * the thing is acted on; the page re-reads the server, not the row.
 *
 * Sound and desktop notices are per-browser conveniences, kept in localStorage. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listNotices, type Notice } from '../data/agency';
import { errorText } from '../data/words';
import { Icon } from '../ui';
import { useProfile } from './profile';
import css from './AppShell.module.css';

const MUTE_KEY = 'jd.agency.muted';
const BASE_TITLE = 'Apkicare Agency';

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMuted(on: boolean) {
  try {
    if (on) localStorage.setItem(MUTE_KEY, '1');
    else localStorage.removeItem(MUTE_KEY);
  } catch {
    // A browser that will not store the choice just forgets it on reload.
  }
}

/* ---------------------------------------------------------------- the cue
 * Built, not loaded: a sound file is a request that can fail silently. One
 * AudioContext, created inside the first user gesture. */
let audio: AudioContext | null = null;

function unlockAudio(): boolean {
  try {
    if (!audio) {
      const Ctx =
        window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return false;
      audio = new Ctx();
    }
    if (audio.state === 'suspended') void audio.resume();
    return true;
  } catch {
    return false;
  }
}

type CueKind = 'request' | 'join' | 'overdue' | 'soft';

/** A rising two-note chime for a request (twice), one note for a join request,
 *  a falling pair (twice) for a job nobody is on, and one short quiet note for
 *  a new win to staff: unlike the console's falling SOS beeps, and over in
 *  under a second. */
function cue(kind: CueKind) {
  if (!audio || audio.state !== 'running') return;
  try {
    const t0 = audio.currentTime + 0.02;
    const notes: [number, number][] =
      kind === 'request'
        ? [
            [0, 660],
            [0.16, 880],
            [0.45, 660],
            [0.61, 880],
          ]
        : kind === 'overdue'
          ? [
              [0, 880],
              [0.16, 660],
              [0.45, 880],
              [0.61, 660],
            ]
          : kind === 'soft'
            ? [[0, 590]]
            : [[0, 740]];
    const peak = kind === 'soft' ? 0.06 : 0.2;
    const tail = kind === 'soft' ? 0.18 : 0.28;
    for (const [dt, f] of notes) {
      const at = t0 + dt;
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(peak, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + tail);
      osc.connect(gain).connect(audio.destination);
      osc.start(at);
      osc.stop(at + 0.3);
    }
  } catch {
    // The rail's count is the record; the sound is only the nudge.
  }
}

function desktopNotice(title: string, body: string, to: string, go: (to: string) => void) {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    // Shown only when the desk is not being looked at: on screen, the rail says it.
    if (document.visibilityState === 'visible' && document.hasFocus()) return;
    const n = new Notification(title, { body, tag: `jd-agency-${to}` });
    n.onclick = () => {
      window.focus();
      go(to);
      n.close();
    };
  } catch {
    // Some browsers refuse notices outside a service worker; the title still says it.
  }
}

/** Where a notice is acted on. */
export const KIND_ROUTE: Record<string, string> = {
  OFFER_RECEIVED: '/requests',
  QUOTE_WON: '/assign',
  ITEM_CANCELLED: '/assign',
  ASSIGNMENT_DECLINED: '/assign',
  // 000079: a worker never answered the agency's proposal; a won job is past
  // its staffing deadline with nobody on it. Both are fixed on Assign.
  STAFF_OFFER_EXPIRED: '/assign',
  AGENCY_UNSTAFFED: '/assign',
  // 000097: a nurse still checked in after her shift: checked out on Assign.
  CHECKOUT_OVERDUE: '/assign',
  JOIN_REQUESTED: '/team',
  AGENCY_APPROVED: '/overview',
  AGENCY_REJECTED: '/documents',
  PAYMENT_RECEIVED: '/earnings',
};

export function ago(iso: string): string {
  const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} days ago`;
}

/** The sound, the desktop notice and the title. Mounted once, in the shell. */
export function useArrivalAlerts(muted: boolean) {
  const { profile } = useProfile();
  const navigate = useNavigate();
  const {
    open_offers: offers,
    join_requests: joins,
    to_staff: toStaff,
    overdue_to_staff: overdueRaw,
    checkout_overdue: checkoutRaw,
  } = profile.counts;
  // An older server has no overdue count: read as none, never as a rise.
  const overdue = overdueRaw ?? 0;
  const checkout = checkoutRaw ?? 0;
  // null until the first read: work already waiting at sign-in is shown, not rung.
  const last = useRef<{ offers: number; joins: number; toStaff: number; overdue: number; checkout: number } | null>(null);
  const owed = useRef<CueKind | null>(null);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  useEffect(() => {
    const prev = last.current;
    last.current = { offers, joins, toStaff, overdue, checkout };
    if (!prev) return;
    const moreOffers = offers > prev.offers;
    const moreJoins = joins > prev.joins;
    const moreOverdue = overdue > prev.overdue;
    const moreToStaff = toStaff > prev.toStaff;
    const moreCheckout = checkout > prev.checkout;
    if (!moreOffers && !moreJoins && !moreOverdue && !moreToStaff && !moreCheckout) return;
    // The loudest reason wins; one cue per read.
    const kind: CueKind = moreOffers ? 'request' : moreOverdue || moreCheckout ? 'overdue' : moreJoins ? 'join' : 'soft';
    if (!mutedRef.current) {
      if (audio?.state === 'running') cue(kind);
      else if (kind !== 'soft' || !owed.current) owed.current = kind;
    }
    if (moreOffers) {
      const n = offers - prev.offers;
      desktopNotice(
        n === 1 ? 'A new request' : `${n} new requests`,
        'A family near you needs care. The first to accept gets the job.',
        '/requests',
        navigate,
      );
    }
    if (moreOverdue) {
      const n = overdue - prev.overdue;
      desktopNotice(
        n === 1 ? 'A job won with nobody assigned' : `${n} jobs won with nobody assigned`,
        'Its staffing deadline has passed. Open Assign and put someone on it.',
        '/assign',
        navigate,
      );
    }
    if (moreCheckout) {
      desktopNotice(
        'A nurse has not checked out',
        'Her shift has ended. Call her, then check her out on her behalf in Assign.',
        '/assign',
        navigate,
      );
    }
    if (moreJoins && !moreOffers && !moreOverdue) {
      desktopNotice('Someone asked to join your agency', 'Open Team to confirm or decline them.', '/team', navigate);
    }
    // only to_staff rose: the soft note above, and no desktop notice.
  }, [offers, joins, toStaff, overdue, checkout, navigate]);

  // The first gesture arms the sound, and plays a cue that was owed.
  useEffect(() => {
    const arm = () => {
      if (!unlockAudio()) return;
      const k = owed.current;
      if (k && !mutedRef.current) {
        owed.current = null;
        window.setTimeout(() => cue(k), 60);
      }
    };
    window.addEventListener('pointerdown', arm);
    window.addEventListener('keydown', arm);
    return () => {
      window.removeEventListener('pointerdown', arm);
      window.removeEventListener('keydown', arm);
    };
  }, []);

  // A hidden tab still says it: the open requests lead the title.
  useEffect(() => {
    document.title = offers > 0 ? `(${offers}) Requests · ${BASE_TITLE}` : BASE_TITLE;
    return () => {
      document.title = BASE_TITLE;
    };
  }, [offers]);
}

/** The bell in the header: what the platform told this agency, and the switches. */
export function Bell() {
  const navigate = useNavigate();
  const [muted, setMuted] = useState(readMuted);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Notice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );

  useArrivalAlerts(muted);

  const read = useCallback(async () => {
    try {
      setRows(await listNotices());
      setError(null);
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  useEffect(() => {
    if (open) void read();
  }, [open, read]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    writeMuted(next);
    if (!next) {
      unlockAudio();
      window.setTimeout(() => cue('join'), 60); // so they hear what it sounds like
    }
  };

  const askPermission = async () => {
    if (typeof Notification === 'undefined') return;
    try {
      setPermission(await Notification.requestPermission());
    } catch {
      setPermission(Notification.permission);
    }
  };

  function go(to: string) {
    setOpen(false);
    navigate(to);
  }

  return (
    <div className={css.who}>
      <button
        type="button"
        className={css.bellBtn}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="What the platform told you"
        title="What the platform told you"
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="bell" size={18} />
        {muted ? <span className={css.bellMuted} aria-label="sound off" /> : null}
      </button>
      {open ? (
        <>
          <button className={css.menuScrim} type="button" aria-label="Close" onClick={() => setOpen(false)} />
          <div className={`${css.menu} ${css.bellPanel}`} role="dialog" aria-label="Notifications">
            <div className={css.menuSection}>
              <div className={css.menuLabel}>Alerts on this computer</div>
              <div className={css.bellSwitches}>
                <button type="button" className={css.bellSwitch} aria-pressed={!muted} onClick={toggleMute}>
                  {muted ? 'Sound off' : 'Sound on'}
                </button>
                {permission === 'granted' ? (
                  <span className={css.bellState}>Desktop alerts on</span>
                ) : permission === 'denied' ? (
                  <span className={css.bellState}>Desktop alerts blocked in the browser's site settings</span>
                ) : permission === 'unsupported' ? null : (
                  <button type="button" className={css.bellSwitch} onClick={() => void askPermission()}>
                    Turn on desktop alerts
                  </button>
                )}
              </div>
              <div className={css.menuHint}>
                A chime plays when a new request or join request arrives, or a job you won passes its staffing deadline
                with nobody assigned; a soft note when a new job is won and needs someone. Keep this tab open. Sound
                starts after your first click on the page.
              </div>
            </div>
            <div className={css.searchGroup}>Recent · last 30 days</div>
            <div className={css.bellList}>
              {error ? (
                <div className={css.searchNone}>{error}</div>
              ) : !rows ? (
                <div className={css.searchNone}>Reading…</div>
              ) : rows.length === 0 ? (
                <div className={css.searchNone}>Nothing yet.</div>
              ) : (
                rows.map((n) => {
                  const to = KIND_ROUTE[n.kind];
                  return (
                    <button
                      key={n.public_id}
                      type="button"
                      className={css.searchHit}
                      disabled={!to}
                      onClick={() => to && go(to)}
                      title={new Date(n.created_at).toLocaleString('en-GB', { timeZone: 'Asia/Kolkata' })}
                    >
                      <span className={css.searchHitTop}>
                        <span className={css.searchRef}>
                          {n.urgency === 'URGENT' ? <span className={css.bellUrgent}>Urgent · </span> : null}
                          {n.title}
                        </span>
                        <span className={css.searchMeta}>{ago(n.created_at)}</span>
                      </span>
                      <span className={css.searchSub}>{n.body}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
